// Typography rules (see src/design-system/TYPOGRAPHY_AND_THEME.md):
//   - Every title uses a typography.h* preset, OR `font-heading font-bold` directly.
//   - Body text inherits --font-body automatically; do not set fontFamily.
//   - Brand wordmark (myvitae.ai) keeps Quicksand via logoFontStyle — don't touch.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  DangerButton,
  FormField,
  Input,
  Modal,
  PrimaryButton,
  SecondaryButton,
  TextArea,
} from "../../../design-system";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import { adminAutomationPreview } from "../../utils/adminApi";
import { useCreateAdminAutomation, useUpdateAdminAutomation } from "../../hooks/useAdminAutomations";
import ActionsEditor from "./ActionsEditor";
import AutomationStateBadge from "./AutomationStateBadge";
import ConditionPicker from "./ConditionPicker";
import DeliveryPolicyFields from "./DeliveryPolicyFields";
import GuardsEditor from "./GuardsEditor";
import LivePreviewPanel from "./LivePreviewPanel";
import TriggerPicker from "./TriggerPicker";
import {
  automationToForm,
  emptyAutomationForm,
  errorsByField,
  formToPayload,
  getStateMeta,
  slugify,
  validateAutomationForm,
} from "./automationUtils";

const PREVIEW_DEBOUNCE_MS = 600;

const ROUTINE_STEPS = [
  { key: "trigger", label: "Trigger", hint: "When the scheduler looks." },
  { key: "situation", label: "Situation", hint: "Who is in this situation right now." },
  { key: "guards", label: "Guards", hint: "Only if… (per user, all must pass)." },
  { key: "actions", label: "Actions", hint: "The messages to send." },
  { key: "policy", label: "Delivery policy", hint: "Priority, caps, quiet hours." },
];

// Event automations have no situation step: the event already names the user.
const EVENT_STEPS = [
  { key: "trigger", label: "Trigger", hint: "Which event fires it." },
  { key: "guards", label: "Guards", hint: "Only if… (per user, all must pass)." },
  { key: "actions", label: "Actions", hint: "The messages to send." },
  { key: "policy", label: "Delivery policy", hint: "Priority, caps, quiet hours." },
];

const sectionTitleClass = `${typography.h5} ${textColors.primary}`;
const sectionCardClass = `rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-4 sm:p-5`;

const Section = ({ id, step, title, description, children }) => (
  <section id={id} aria-labelledby={`${id}-title`} className={sectionCardClass}>
    <div className="flex items-start gap-3 mb-4">
      {step ? (
        <span
          className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 dark:bg-blue-500 text-white text-xs font-semibold"
          aria-hidden="true"
        >
          {step}
        </span>
      ) : null}
      <div className="min-w-0">
        <h3 id={`${id}-title`} className={sectionTitleClass}>
          {title}
        </h3>
        {description ? <p className={`mt-0.5 ${typography.caption} ${textColors.tertiary}`}>{description}</p> : null}
      </div>
    </div>
    {children}
  </section>
);

Section.propTypes = {
  id: PropTypes.string.isRequired,
  step: PropTypes.number,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  children: PropTypes.node.isRequired,
};

/**
 * Create / edit / duplicate an automation in a full-width modal. The left
 * column walks Trigger → Situation → Guards → Actions → Delivery policy; the
 * right column is the debounced live preview of the current form.
 */
const AutomationEditor = ({ isOpen, mode = "create", automation = null, meta, onClose, onSaved }) => {
  const createMutation = useCreateAdminAutomation();
  const updateMutation = useUpdateAdminAutomation();
  const isSaving = createMutation.isPending || updateMutation.isPending;

  const [form, setForm] = useState(() => emptyAutomationForm(meta));
  const [initialSnapshot, setInitialSnapshot] = useState("");
  const [saveError, setSaveError] = useState("");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [preview, setPreview] = useState({ status: "idle", data: null, error: "", key: "" });
  const previewRequestRef = useRef(0);
  const errorBannerRef = useRef(null);

  // (Re)initialise the form whenever the editor opens for a given automation/mode.
  useEffect(() => {
    if (!isOpen) return;
    const next =
      mode === "create" || !automation
        ? emptyAutomationForm(meta)
        : automationToForm(automation, meta, { duplicate: mode === "duplicate" });
    setForm(next);
    setInitialSnapshot(JSON.stringify(formToPayload(next)));
    setSaveError("");
    setConfirmDiscard(false);
    setPreview({ status: "idle", data: null, error: "", key: "" });
    previewRequestRef.current += 1;
    // `meta` is intentionally not a dependency: it only seeds defaults.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, automation]);

  const payload = useMemo(() => formToPayload(form), [form]);
  const payloadKey = useMemo(() => JSON.stringify(payload), [payload]);
  const isDirty = payloadKey !== initialSnapshot;
  const clientErrors = useMemo(() => validateAutomationForm(form, meta), [form, meta]);
  const fieldErrors = useMemo(() => errorsByField(clientErrors), [clientErrors]);
  const isClientValid = clientErrors.length === 0;
  const isEditingSaved = mode === "edit" && form.id != null;

  // Live preview: debounce the current payload → POST preview (with `id` when editing).
  useEffect(() => {
    if (!isOpen) return undefined;
    if (!isClientValid) {
      setPreview((prev) => ({ ...prev, status: "invalid", error: "" }));
      return undefined;
    }
    let cancelled = false;
    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    const timer = setTimeout(async () => {
      setPreview((prev) => ({ ...prev, status: "loading", error: "" }));
      try {
        const body = isEditingSaved ? { ...payload, id: form.id } : payload;
        const data = await adminAutomationPreview(body);
        if (cancelled || previewRequestRef.current !== requestId) return;
        setPreview({ status: "success", data, error: "", key: payloadKey });
      } catch (err) {
        if (cancelled || previewRequestRef.current !== requestId) return;
        setPreview((prev) => ({
          status: "error",
          data: prev.data,
          error: err?.message || "Unknown error",
          key: payloadKey,
        }));
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // payloadKey captures every payload change; form.id / isEditingSaved are folded into it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, payloadKey, isClientValid]);

  const previewIsStale = preview.status !== "invalid" && preview.key !== "" && preview.key !== payloadKey;
  const previewRejectedCurrent = preview.status === "error" && preview.key === payloadKey;
  const canSave = isClientValid && !previewRejectedCurrent && !isSaving && (mode !== "edit" || isDirty);

  const patch = useCallback((changes) => setForm((prev) => ({ ...prev, ...changes })), []);

  const handleNameChange = (value) => {
    setForm((prev) => ({
      ...prev,
      name: value,
      slug: prev.slugTouched ? prev.slug : slugify(value),
    }));
  };

  const requestClose = () => {
    if (isSaving) return;
    if (isDirty) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaveError("");
    try {
      const result = isEditingSaved
        ? await updateMutation.mutateAsync({ id: form.id, payload })
        : await createMutation.mutateAsync({ ...payload, state: "draft" });
      onSaved?.(result?.automation || null, mode);
    } catch (err) {
      setSaveError(err?.message || "Failed to save automation.");
      setTimeout(() => errorBannerRef.current?.focus?.(), 0);
    }
  };

  const title =
    mode === "edit" ? "Edit automation" : mode === "duplicate" ? "Duplicate automation" : "New automation";

  const isEventTrigger = form.trigger_type === "event";
  const steps = isEventTrigger ? EVENT_STEPS : ROUTINE_STEPS;
  const stepNumber = (key) => steps.findIndex((step) => step.key === key) + 1;

  const extraSlugOptions = useMemo(() => {
    const slug = String(form.slug || "").trim();
    if (!slug) return [];
    const known = (meta?.automation_slugs || []).some((a) => a.slug === slug);
    return known ? [] : [{ slug, label: `${slug} (this automation)` }];
  }, [form.slug, meta]);

  const footer = (
    <div className="w-full flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className={`${typography.caption} ${textColors.tertiary} min-w-0`}>
        {confirmDiscard ? null : isEditingSaved ? (
          <span className="inline-flex items-center gap-2 flex-wrap">
            <AutomationStateBadge state={form.state} />
            <span>{getStateMeta(form.state).description}</span>
          </span>
        ) : (
          <span>
            Saved as <strong>Draft</strong> — drafts never send. Activate it from the list once the preview looks right.
          </span>
        )}
      </div>
      {confirmDiscard ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2" role="group" aria-label="Discard unsaved changes">
          <span className={`${typography.bodySmall} ${textColors.primary}`}>Discard unsaved changes?</span>
          <SecondaryButton size="sm" onClick={() => setConfirmDiscard(false)} autoFocus>
            Keep editing
          </SecondaryButton>
          <DangerButton size="sm" variant="outline" onClick={onClose}>
            Discard
          </DangerButton>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <SecondaryButton onClick={requestClose} disabled={isSaving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleSave} disabled={!canSave} loading={isSaving}>
            {isEditingSaved ? "Save changes" : "Save as draft"}
          </PrimaryButton>
        </div>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={requestClose}
      title={title}
      size="xl"
      className="mx-4 lg:max-w-6xl"
      bodyMaxHeight="calc(100vh - 200px)"
      closeOnOverlayClick={false}
      footer={footer}
    >
      <div className="space-y-4">
        {/* Mental model strip */}
        <ol className="flex flex-wrap items-center gap-x-2 gap-y-1" aria-label="How an automation works">
          {steps.map((step, index) => (
            <li key={step.key} className="inline-flex items-center gap-2">
              <span className={`${typography.caption} ${textColors.secondary}`} title={step.hint}>
                <span className={`font-semibold ${textColors.primary}`}>{index + 1}.</span> {step.label}
              </span>
              {index < steps.length - 1 ? (
                <span className={`${typography.caption} ${textColors.muted}`} aria-hidden="true">
                  →
                </span>
              ) : null}
            </li>
          ))}
        </ol>

        {saveError ? (
          <div
            ref={errorBannerRef}
            tabIndex={-1}
            role="alert"
            className="rounded-2xl px-4 py-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400"
          >
            <p className={`${typography.bodySmall} text-red-800 dark:text-red-300 break-words`}>{saveError}</p>
          </div>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Section id="automation-basics" title="Basics" description="How this automation shows up in the list and in message history.">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Name" htmlFor="automation-name" required>
                  <Input
                    id="automation-name"
                    value={form.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="e.g. Plan expiring reminder"
                    disabled={isSaving}
                    error={form.name ? fieldErrors.name || "" : ""}
                  />
                </FormField>
                <FormField
                  label="Slug"
                  htmlFor="automation-slug"
                  required
                  helperText="Stable identifier other automations can reference (guards). Letters, digits, hyphens, underscores."
                >
                  <Input
                    id="automation-slug"
                    value={form.slug}
                    onChange={(e) => patch({ slug: e.target.value, slugTouched: true })}
                    placeholder="plan-expiring-reminder"
                    disabled={isSaving}
                    error={form.slug ? fieldErrors.slug || "" : ""}
                    className="font-mono"
                  />
                </FormField>
              </div>
              <div className="mt-4">
                <FormField label="Description" htmlFor="automation-description" optional>
                  <TextArea
                    id="automation-description"
                    value={form.description}
                    onChange={(e) => patch({ description: e.target.value })}
                    rows={2}
                    placeholder="What this automation is for — shown to admins only."
                    disabled={isSaving}
                  />
                </FormField>
              </div>
            </Section>

            <Section
              id="automation-trigger"
              step={stepNumber("trigger")}
              title="Trigger"
              description={
                isEventTrigger
                  ? "Fires the moment the chosen event happens to a user — every occurrence, no schedule."
                  : "A routine schedule: the days of the week and the hour (UTC) the situation is evaluated."
              }
            >
              <TriggerPicker
                triggerType={form.trigger_type}
                triggerEvent={form.trigger_event}
                days={form.run_days_of_week}
                hour={form.run_hour_utc}
                events={meta?.events || []}
                onTypeChange={(type) => patch({ trigger_type: type })}
                onEventChange={(key) => patch({ trigger_event: key })}
                onScheduleChange={({ days, hour }) => patch({ run_days_of_week: days, run_hour_utc: hour })}
                disabled={isSaving}
                errors={fieldErrors}
              />
            </Section>

            {isEventTrigger ? null : (
            <Section id="automation-situation" step={stepNumber("situation")} title="Situation" description="One code-defined condition with parameters. It selects who is in this situation right now; users fire once, when they enter it.">
              <ConditionPicker
                id="automation-situation-key"
                label="Situation"
                conditions={meta?.situations || []}
                value={form.situation_key}
                params={form.situation_params}
                onChange={(key, params) => patch({ situation_key: key, situation_params: params })}
                onParamChange={(name, value) =>
                  setForm((prev) => ({ ...prev, situation_params: { ...(prev.situation_params || {}), [name]: value } }))
                }
                automationSlugs={meta?.automation_slugs || []}
                extraSlugOptions={extraSlugOptions}
                placeholder="Choose a situation…"
                disabled={isSaving}
                error={form.situation_key ? fieldErrors.situation_key || "" : ""}
                paramErrors={Object.fromEntries(
                  Object.entries(fieldErrors)
                    .filter(([field]) => field.startsWith("situation_params."))
                    .map(([field, message]) => [field.slice("situation_params.".length), message])
                )}
                required
              />
            </Section>
            )}

            <Section id="automation-guards" step={stepNumber("guards")} title="Guards" description={isEventTrigger ? "Per-user checks evaluated for the event's user right before enqueuing. All must pass (AND)." : "Optional extra per-user checks, evaluated right before enqueuing. All must pass (AND)."}>
              <GuardsEditor
                guards={form.guards}
                conditions={meta?.guards || []}
                automationSlugs={meta?.automation_slugs || []}
                extraSlugOptions={extraSlugOptions}
                onChange={(guards) => patch({ guards })}
                disabled={isSaving}
                errors={fieldErrors}
              />
            </Section>

            <Section id="automation-actions" step={stepNumber("actions")} title="Actions" description={isEventTrigger ? "The messages sent to the event's user when the guards pass. Delays are 'no earlier than' offsets from the moment the event happens." : "The messages sent to each user who enters the situation and passes the guards. Delays are 'no earlier than' offsets from entry."}>
              <ActionsEditor
                actions={form.actions}
                variants={meta?.email_template_variants || []}
                variables={meta?.variables || []}
                defaultSender={meta?.default_sender_display_name || ""}
                onChange={(actions) => patch({ actions })}
                disabled={isSaving}
                errors={fieldErrors}
              />
            </Section>

            <Section id="automation-policy" step={stepNumber("policy")} title="Delivery policy" description="How this automation interacts with the platform-wide frequency caps and quiet hours.">
              <DeliveryPolicyFields
                priority={form.priority}
                ignoreFrequencyCaps={form.ignore_frequency_caps}
                respectQuietHours={form.respect_quiet_hours}
                onChange={patch}
                disabled={isSaving}
                errors={fieldErrors}
              />
            </Section>
          </div>

          <div className="lg:col-span-1">
            <LivePreviewPanel
              className="lg:sticky lg:top-0"
              status={preview.status}
              data={preview.data}
              error={preview.error}
              clientErrors={clientErrors}
              isEditingSaved={isEditingSaved}
              stale={previewIsStale}
              triggerType={form.trigger_type}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

AutomationEditor.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["create", "edit", "duplicate"]),
  automation: PropTypes.object,
  meta: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  /** (savedAutomation, mode) */
  onSaved: PropTypes.func,
};

export default AutomationEditor;
